import React, { useState, useEffect, useCallback } from "react";
import { Box, Chip, Typography, useTheme, InputBase, IconButton, Button } from "@mui/material";
import { DataGrid, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarDensitySelector, GridToolbarExport } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { Link, useParams, useLocation } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import MapIcon from "@mui/icons-material/Map";
import { API_BASE_URL } from "../../config";

const Rides = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { id } = useParams();
  const location = useLocation();

  const isDriverFilter = location.pathname.includes("/driver/rides");
  const isUserFilter = location.pathname.includes("/user/rides");

  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [rowCount, setRowCount] = useState(0);
  const [search, setSearch] = useState("");

  const fetchRides = useCallback(async () => {
    setLoading(true);
    try {
      const authToken = sessionStorage.getItem("authToken");
      const payload = {
        authToken,
        page,
        search,
        limit: 20
      };

      if (isDriverFilter && id) {
        payload.driver_id = id;
      } else if (isUserFilter && id) {
        payload.user_id = id;
      }

      const response = await fetch(`${API_BASE_URL}/admin/rides`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch rides");
      }

      const data = await response.json();
      const list = data.data || data.rides || [];
      setRides(list);
      setRowCount(data.total || list.length);
    } catch (error) {
      console.error("Error fetching rides:", error);
      setRides([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, id, isDriverFilter, isUserFilter]);

  useEffect(() => {
    fetchRides();
  }, [fetchRides]);

  const getStatusChip = (state) => {
    switch (state) {
      case 0:
        return <Chip label="⌛ En attente" size="small" sx={{ backgroundColor: "#f57c00", color: "#fff" }} />;
      case 1:
        return <Chip label="🚗 En cours" size="small" sx={{ backgroundColor: colors.blueAccent[600], color: "#fff" }} />;
      case 2:
        return <Chip label="✔️ Terminée" size="small" sx={{ backgroundColor: colors.greenAccent[600], color: "#fff" }} />;
      case 3:
        return <Chip label="❌ Annulée" size="small" sx={{ backgroundColor: "#d32f2f", color: "#fff" }} />;
      default:
        return <Chip label="Inconnu" size="small" />;
    }
  };

  const getCarLabel = (car) => {
    const cars = { 1: "Classic", 2: "Confort", 3: "VIP", 4: "Woman", 5: "Express" };
    return cars[car] || "Classic";
  };

  const columns = [
    { field: "id", headerName: "ID Course", flex: 0.8 },
    {
      field: "passenger_name",
      headerName: "Passager",
      flex: 1,
      renderCell: (params) => (
        <Box>
          <Typography fontWeight="600" color={colors.grey[100]}>
            {params.row.passenger_name || params.row.user_id}
          </Typography>
          {params.row.passenger_phone && (
            <Typography variant="caption" color={colors.grey[300]}>
              {params.row.passenger_phone}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "driver_name",
      headerName: "Chauffeur",
      flex: 1,
      renderCell: (params) => (
        <Box>
          <Typography fontWeight="600" color={colors.greenAccent[400]}>
            {params.row.driver_name ? `${params.row.driver_name} ${params.row.driver_fname || ''}` : "Non assigné"}
          </Typography>
          {params.row.driver_phone && (
            <Typography variant="caption" color={colors.grey[300]}>
              {params.row.driver_phone}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "car",
      headerName: "Catégorie",
      flex: 0.8,
      renderCell: (params) => (
        <Chip label={getCarLabel(params.row.car)} variant="outlined" size="small" sx={{ borderColor: colors.grey[400], color: colors.grey[100] }} />
      ),
    },
    {
      field: "price",
      headerName: "Prix",
      flex: 0.8,
      renderCell: (params) => (
        <Typography fontWeight="bold" color={colors.greenAccent[500]}>
          {params.value} DZD
        </Typography>
      ),
    },
    {
      field: "distance",
      headerName: "Distance",
      flex: 0.6,
      renderCell: (params) => `${params.value || 0} km`,
    },
    {
      field: "duration",
      headerName: "Durée",
      flex: 0.6,
      renderCell: (params) => `${params.value || 0} min`,
    },
    {
      field: "state",
      headerName: "Statut",
      flex: 0.9,
      renderCell: (params) => getStatusChip(params.value),
    },
    {
      field: "created_at",
      headerName: "Date & Heure",
      flex: 1.1,
      renderCell: (params) => (
        <Typography variant="body2" color={colors.grey[200]}>
          {params.value ? new Date(params.value).toLocaleDateString('fr-FR', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
          }) : '-'}
        </Typography>
      ),
    },
    {
      field: "map",
      headerName: "Tracé",
      flex: 0.6,
      renderCell: ({ row: { id } }) => (
        <IconButton component={Link} to={`/ride/${id}`} color="primary" sx={{ color: colors.greenAccent[400] }}>
          <MapIcon />
        </IconButton>
      ),
    },
  ];

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
      </GridToolbarContainer>
    );
  }

  let subtitleText = "Historique et statut de toutes les courses LinkerVTC";
  if (isDriverFilter) subtitleText = `Filtré pour le chauffeur ID : ${id}`;
  if (isUserFilter) subtitleText = `Filtré pour le passager ID : ${id}`;

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap="10px">
        <Header title="COURSES & TRAJETS" subtitle={subtitleText} />
        {(isDriverFilter || isUserFilter) && (
          <Button component={Link} to="/rides" variant="outlined" color="secondary">
            ← Toutes les courses
          </Button>
        )}
      </Box>

      <Box display="flex" backgroundColor={colors.primary[400]} borderRadius="3px" mt="15px">
        <InputBase
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ ml: 2, flex: 1 }}
          placeholder="Rechercher par passager, chauffeur, téléphone..."
        />
        <IconButton type="button" sx={{ p: 1 }}>
          <SearchIcon />
        </IconButton>
      </Box>

      <Box
        m="20px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.blueAccent[700], borderBottom: "none" },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": { borderTop: "none", backgroundColor: colors.blueAccent[700] },
          "& .MuiCheckbox-root": { color: `${colors.greenAccent[200]} !important` },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": { color: `${colors.grey[100]} !important` },
        }}
      >
        <DataGrid
          rows={rides}
          columns={columns}
          slots={{ toolbar: CustomToolbar }}
          pagination
          rowCount={rowCount}
          loading={loading}
          paginationMode="server"
          onPaginationModelChange={(newModel) => setPage(newModel.page + 1)}
        />
      </Box>
    </Box>
  );
};

export default Rides;
